import { MouseEvent, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { NodeProps } from "@xyflow/react";
import { motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import __ from "src/utils/Translations";
import { Avatar, PresenceIndicator } from "@benefit-systems/hr-tools-ui";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import { TEmployeeNode } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Nodes/customNodes.models";
import TopHandle from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Nodes/shared-components/TopHandle";
import useOrgchartEmployeesStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/store";
import useSearchedNodesStyles from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/hooks/useSearchedNodesStyles";
import BottomHandle from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/handles/BottomHandle";
import LoadMoreHandle from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/handles/LoadMoreHandle";
import SearchLoadMoreHandle from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/handles/SearchLoadMoreHandle";
import {
  fetchEmployeeOrgchart,
  fetchEmployeeOrgchartWithExclusions,
  processChildrenNodes,
  IEmployeeInOrgchartChild,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/api";

const Node = ({ data, id: nodeId }: NodeProps<TEmployeeNode>) => {
  const companyId = useCompanyId();
  const isAhr = useIsAhr();
  const structureId = useStructureStore((state) => state.structureId) || "";
  const { resetField } = useFormContext();
  const { nodes, wasSearchTriggered } = useOrgchartEmployeesStore();

  const searchedStyles = useSearchedNodesStyles(nodeId);
  const supervisorId = data.parentId || "root";

  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
    useOrgchartEmployeesStore.getState().setSearchedNodeId(null);
  };

  const handleLoadMore = async (parentSupervisorId: string, cursor: string) => {
    const store = useOrgchartEmployeesStore.getState();

    // If in search mode, use exclusion API
    if (wasSearchTriggered && store.hierarchyData) {
      store.setSearchedNodeId(null);
      store.setWasSearchedNodeCleared(true);
      resetField("autocomplete", { defaultValue: "" });

      const extractEmployeeIds = (
        children: IEmployeeInOrgchartChild[]
      ): string[] => {
        const ids: string[] = [];
        children.forEach((child) => {
          ids.push(child.employeeId);
          if (child.children?.length) {
            ids.push(...extractEmployeeIds(child.children));
          }
        });
        return ids;
      };

      const excludeIds = extractEmployeeIds(store.hierarchyData.children);
      const response = await fetchEmployeeOrgchartWithExclusions(
        companyId,
        structureId,
        parentSupervisorId,
        excludeIds,
        cursor
      );
      const { nodes: newNodes, edges: newEdges } = processChildrenNodes(
        response,
        parentSupervisorId
      );

      store.setNodes([...store.nodes, ...newNodes]);
      store.setEdges([...store.edges, ...newEdges]);

      if (response.nextCursor) {
        store.setPaginationCursor(parentSupervisorId, response.nextCursor);
      } else {
        store.paginationCursors.delete(parentSupervisorId);
      }

      return newNodes.map((n) => n.id);
    }

    // Normal mode - no exclusions
    const response = await fetchEmployeeOrgchart(
      companyId,
      structureId,
      cursor,
      parentSupervisorId
    );
    const { nodes: newNodes, edges: newEdges } = processChildrenNodes(
      response,
      parentSupervisorId
    );

    store.setNodes([...store.nodes, ...newNodes]);
    store.setEdges([...store.edges, ...newEdges]);

    if (response.nextCursor) {
      store.setPaginationCursor(parentSupervisorId, response.nextCursor);
    } else {
      store.paginationCursors.delete(parentSupervisorId);
    }

    return newNodes.map((n) => n.id);
  };

  const nextCursor = useOrgchartEmployeesStore((state) =>
    state.paginationCursors.get(supervisorId || "root")
  );

  const siblings = useMemo(
    () =>
      nodes.filter(
        (n) =>
          n.data.supervisorId === supervisorId ||
          (!n.data.supervisorId && !supervisorId)
      ),
    [nodes, supervisorId]
  );

  const parentNode = nodes.find((n) => n.id === (supervisorId || "root"));
  const isLastChild =
    siblings.length > 0 && siblings[siblings.length - 1].id === nodeId;

  const parentSubordinatesCount =
    typeof parentNode?.data?.subordinateCount === "number"
      ? parentNode.data.subordinateCount
      : 0;
  const hasMoreSubordinates = parentSubordinatesCount > siblings.length;

  const shouldShowLoadMore =
    isLastChild &&
    (!!nextCursor || (wasSearchTriggered && hasMoreSubordinates));

  return (
    <div style={{ opacity: searchedStyles.opacity }}>
      <TopHandle />
      <motion.div
        layout
        layoutId={`layoutFor${nodeId}`}
        transition={{ duration: 0.2 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.2, delay: 0.4 } }}
        onClick={stopPropagation}
      >
        <Box
          sx={{
            borderRadius: "12px",
            padding: "0",
            width: "340px",
            minWidth: "340px",
            maxWidth: "340px",
            border: searchedStyles.border,
            boxShadow:
              "0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12);",
          }}
        >
          <Box
            sx={{
              padding: "16px 16px 24px 16px",
              height: data.position ? "auto" : "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: data.position ? "flex-start" : "center",
                gap: "8px",
                height: data.position ? "auto" : "100%",
              }}
            >
              <Box>
                <Link
                  to={
                    isAhr
                      ? `/ahr/employees/${nodeId}`
                      : `/company/edit/${companyId}/employee-management/employees/${nodeId}`
                  }
                  style={{ textDecoration: "none" }}
                >
                  <PresenceIndicator status={data?.presenceInformation?.status}>
                    <Avatar size={13} src={data.avatar} alt="ManagerPhoto">
                      {data.initials}
                    </Avatar>
                  </PresenceIndicator>
                </Link>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: "100%",
                }}
              >
                <Link
                  to={
                    isAhr
                      ? `/ahr/employees/${nodeId}`
                      : `/company/edit/${companyId}/employee-management/employees/${nodeId}`
                  }
                  style={{ textDecoration: "none" }}
                >
                  <Typography sx={{ color: "text.primary" }} variant="h5">
                    {data.name}
                  </Typography>
                </Link>
                {data.fk && (
                  <Typography color="textSecondary" variant="body2">
                    (FK {__(`${data.fk}`)})
                  </Typography>
                )}
                {data.position && (
                  <Typography color="textSecondary" variant="body2">
                    {typeof data.position === "string"
                      ? __(`${data.position}`)
                      : __(
                          `${
                            (data.position as { id: string; label: string })
                              .label
                          }`
                        )}
                  </Typography>
                )}
                {data.area &&
                  (typeof data.area === "string" ? (
                    <Typography color="textSecondary" variant="body2">
                      {__(`${data.area}`)}
                    </Typography>
                  ) : (
                    <Link
                      to={
                        isAhr
                          ? `/ahr/organization-structure/configure-structure/area/${data.area.id}`
                          : `/company/edit/${companyId}/organization-structure/configure-structure/area/${data.area.id}`
                      }
                      style={{ textDecoration: "none" }}
                    >
                      <Typography color="textSecondary" variant="body2">
                        {__(`${data.area.label}`)}
                      </Typography>
                    </Link>
                  ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </motion.div>
      {data.isSupervisor && <BottomHandle nodeId={nodeId} />}
      {isLastChild && nextCursor && (
        <LoadMoreHandle
          supervisorId={supervisorId}
          nextCursor={nextCursor}
          onLoadMore={handleLoadMore}
        />
      )}
      {shouldShowLoadMore && !nextCursor && wasSearchTriggered && (
        <SearchLoadMoreHandle supervisorId={supervisorId} />
      )}
    </div>
  );
};

export default Node;
